import { useState } from 'react'
import { CheckCircle2, Circle } from 'lucide-react'

interface PreflightChecklistProps {
  mode: 'install' | 'uninstall'
  deviceName?: string
  onConfirm: () => void
  isLoading?: boolean
  error?: string | null
  helperReady?: boolean
}

export default function PreflightChecklist({
  mode,
  deviceName,
  onConfirm,
  isLoading,
  error,
  helperReady = true,
}: PreflightChecklistProps) {
  const [sdpChecked, setSdpChecked] = useState(false)
  const [findMyChecked, setFindMyChecked] = useState(false)

  const allChecked = sdpChecked && findMyChecked
  const actionWord = mode === 'install' ? 'installation' : 'removal'

  return (
    <div className="flex flex-col min-h-0 flex-1">
      <div className="relative p-4 bg-emerald-100 border-2 border-emerald-400 rounded-xl text-left mb-5">
        <span className="absolute -top-2.5 left-3 px-2 py-0.5 bg-emerald-600 text-white text-xs font-bold uppercase tracking-wider rounded shadow-sm">
          Temporary
        </span>
        <p className="text-base font-semibold text-emerald-900 mt-1">
          Only for {actionWord} - turn both back on right after.
        </p>
        <p className="text-sm text-emerald-800 mt-2">
          Apple requires these off only during {actionWord}. Re-enable them as soon as {mode === 'install' ? 'Unbreakable is installed' : 'Unbreakable is removed'}.
        </p>
      </div>

      <div className="space-y-3 mb-5">
        <button
          type="button"
          onClick={() => setSdpChecked(!sdpChecked)}
          className={`w-full flex items-start gap-4 p-4 rounded-xl border-2 text-left transition-all ${
            sdpChecked
              ? 'border-emerald-400 bg-emerald-50'
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
        >
          <div className="shrink-0 mt-0.5">
            {sdpChecked ? (
              <CheckCircle2 size={22} className="text-emerald-600" />
            ) : (
              <Circle size={22} className="text-gray-300" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900">Stolen Device Protection</p>
            <p className="text-xs text-gray-600 mt-1">
              <strong>Settings &rarr; Face ID &amp; Passcode &rarr; Stolen Device Protection</strong> &rarr; Turn Off
            </p>
          </div>
        </button>

        <button
          type="button"
          onClick={() => setFindMyChecked(!findMyChecked)}
          className={`w-full flex items-start gap-4 p-4 rounded-xl border-2 text-left transition-all ${
            findMyChecked
              ? 'border-emerald-400 bg-emerald-50'
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
        >
          <div className="shrink-0 mt-0.5">
            {findMyChecked ? (
              <CheckCircle2 size={22} className="text-emerald-600" />
            ) : (
              <Circle size={22} className="text-gray-300" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900">Find My iPhone</p>
            <p className="text-xs text-gray-600 mt-1">
              <strong>Settings &rarr; [Your Name] &rarr; Find My &rarr; Find My iPhone</strong> &rarr; Turn Off
            </p>
          </div>
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 text-left">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {!helperReady && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 text-left">
          <p className="text-amber-900 text-sm font-medium">Helper app disconnected</p>
          <p className="text-amber-800 text-xs mt-1">
            Re-open the Helper app and keep the iPhone plugged in before starting {actionWord}.
          </p>
        </div>
      )}

      <button
        onClick={onConfirm}
        disabled={!allChecked || isLoading || !helperReady}
        className="w-full shrink-0 py-3 bg-brand text-white rounded-xl hover:bg-brand-hover transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed mt-auto"
      >
        {isLoading
          ? 'Starting...'
          : !helperReady
            ? 'Open the Helper app to continue'
          : !allChecked
            ? 'Complete both steps above'
            : mode === 'install'
              ? `Install Unbreakable${deviceName ? ` on ${deviceName}` : ''}`
              : `Uninstall Unbreakable${deviceName ? ` from ${deviceName}` : ''}`
        }
      </button>
    </div>
  )
}
